import React, { useState, useRef } from 'react';
import { Upload, FileSpreadsheet, CheckCircle, XCircle, AlertTriangle, Download, Trash2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function PTvsCRR() {
  const [ptFile, setPtFile] = useState(null);
  const [crrFile, setCrrFile] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all');
  const ptRef = useRef(null);
  const crrRef = useRef(null);

  const handleCompare = async () => {
    if (!ptFile || !crrFile) { toast.error('Please upload both files'); return; }
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('pt_file', ptFile);
      formData.append('crr_file', crrFile);
      const res = await axios.post(`${API}/api/warehouse/compare-pt-crr`, formData);
      setResult(res.data);
      toast.success(`Comparison complete: ${res.data.summary.total_codes} items analyzed`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Comparison failed');
    } finally { setLoading(false); }
  };

  const handleExport = async () => {
    if (!result) return;
    try {
      const res = await axios.post(`${API}/api/warehouse/compare-pt-crr/export`, result, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `PT_vs_CRR_${result.pt_info?.project || 'comparison'}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Export failed'); }
  };

  const handleReset = () => {
    setPtFile(null); setCrrFile(null); setResult(null); setFilter('all');
    if (ptRef.current) ptRef.current.value = '';
    if (crrRef.current) crrRef.current.value = '';
  };

  const items = result?.items || [];
  const filtered = filter === 'all' ? items
    : filter === 'match' ? items.filter(i => i.status === 'match')
    : filter === 'mismatch' ? items.filter(i => i.status === 'qty_mismatch')
    : filter === 'pt-only' ? items.filter(i => i.status === 'pt_only')
    : items.filter(i => i.status === 'crr_only');

  const s = result?.summary || {};

  return (
    <div className="space-y-5" data-testid="pt-vs-crr-page">
      {!result ? (
        <div className="max-w-2xl mx-auto space-y-6">
          <div className="text-center py-8">
            <div className="w-16 h-16 rounded-2xl bg-indigo-50 flex items-center justify-center mx-auto mb-4">
              <FileSpreadsheet className="w-8 h-8 text-indigo-500" />
            </div>
            <h2 className="text-xl font-bold text-slate-800">PT vs CRR Comparison</h2>
            <p className="text-sm text-slate-500 mt-1">
              Upload a Pick Ticket Excel and a Component Requirement Report Excel to compare item codes and quantities.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className={`border-2 border-dashed rounded-xl p-5 text-center transition-all ${ptFile ? 'border-amber-300 bg-amber-50' : 'border-slate-200 hover:border-slate-300'}`}>
              <Upload className={`w-6 h-6 mx-auto mb-2 ${ptFile ? 'text-amber-500' : 'text-slate-400'}`} />
              <div className="text-sm font-semibold text-slate-700 mb-1">Pick Ticket Excel</div>
              {ptFile ? (
                <div className="text-xs text-amber-600 font-medium truncate px-2">{ptFile.name}</div>
              ) : (
                <div className="text-[10px] text-slate-400">Exported from Pick Ticket Summary</div>
              )}
              <input ref={ptRef} type="file" accept=".xlsx,.xls" className="hidden"
                data-testid="pt-file-input"
                onChange={e => { if (e.target.files[0]) setPtFile(e.target.files[0]); }} />
              <button onClick={() => ptRef.current?.click()}
                data-testid="pt-upload-btn"
                className="mt-3 px-4 py-1.5 text-xs font-semibold rounded-lg bg-amber-500 text-white hover:bg-amber-600 transition-colors">
                {ptFile ? 'Change File' : 'Select File'}
              </button>
            </div>

            <div className={`border-2 border-dashed rounded-xl p-5 text-center transition-all ${crrFile ? 'border-emerald-300 bg-emerald-50' : 'border-slate-200 hover:border-slate-300'}`}>
              <Upload className={`w-6 h-6 mx-auto mb-2 ${crrFile ? 'text-emerald-500' : 'text-slate-400'}`} />
              <div className="text-sm font-semibold text-slate-700 mb-1">CRR Excel</div>
              {crrFile ? (
                <div className="text-xs text-emerald-600 font-medium truncate px-2">{crrFile.name}</div>
              ) : (
                <div className="text-[10px] text-slate-400">Exported from Component Req. Converter</div>
              )}
              <input ref={crrRef} type="file" accept=".xlsx,.xls" className="hidden"
                data-testid="crr-file-input"
                onChange={e => { if (e.target.files[0]) setCrrFile(e.target.files[0]); }} />
              <button onClick={() => crrRef.current?.click()}
                data-testid="crr-upload-btn"
                className="mt-3 px-4 py-1.5 text-xs font-semibold rounded-lg bg-emerald-500 text-white hover:bg-emerald-600 transition-colors">
                {crrFile ? 'Change File' : 'Select File'}
              </button>
            </div>
          </div>

          <div className="text-center">
            <button onClick={handleCompare} disabled={!ptFile || !crrFile || loading}
              data-testid="compare-btn"
              className="px-8 py-2.5 text-sm font-bold rounded-xl bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-lg shadow-indigo-200">
              {loading ? 'Comparing...' : 'Compare'}
            </button>
          </div>
        </div>
      ) : (
        <>
          {/* Summary Cards */}
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h2 className="text-lg font-bold text-slate-800">Comparison Result</h2>
              <p className="text-xs text-slate-400">
                PT: {result.pt_info?.project || '-'} / Set {result.pt_info?.set || '-'} &nbsp;|&nbsp;
                CRR: {result.crr_info?.bill || '-'}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={handleExport} data-testid="export-btn"
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg bg-indigo-600 text-white hover:bg-indigo-700">
                <Download className="w-3.5 h-3.5" /> Export Excel
              </button>
              <button onClick={handleReset} data-testid="reset-btn"
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg bg-slate-100 text-slate-600 hover:bg-slate-200">
                <Trash2 className="w-3.5 h-3.5" /> New Comparison
              </button>
            </div>
          </div>

          <div className="grid grid-cols-5 gap-3">
            {[
              { key: 'all', label: 'Total Items', value: s.total_codes, color: 'slate', icon: FileSpreadsheet },
              { key: 'match', label: 'Matched', value: s.matched, color: 'emerald', icon: CheckCircle },
              { key: 'mismatch', label: 'Qty Mismatch', value: s.qty_mismatch, color: 'red', icon: XCircle },
              { key: 'pt-only', label: 'PT Only', value: s.pt_only, color: 'amber', icon: AlertTriangle },
              { key: 'crr-only', label: 'CRR Only', value: s.crr_only, color: 'blue', icon: AlertTriangle },
            ].map(c => {
              const Icon = c.icon;
              const isActive = filter === c.key;
              return (
                <button key={c.key} onClick={() => setFilter(c.key)}
                  data-testid={`filter-${c.key}`}
                  className={`p-3 rounded-xl border text-left transition-all ${
                    isActive ? `bg-${c.color}-50 border-${c.color}-300 ring-1 ring-${c.color}-200` : 'bg-white border-slate-200 hover:border-slate-300'
                  }`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className={`text-[10px] uppercase font-bold tracking-wider text-${c.color}-500`}>{c.label}</span>
                    <Icon className={`w-3.5 h-3.5 text-${c.color}-400`} />
                  </div>
                  <div className={`text-2xl font-black text-${c.color}-600`}>{c.value}</div>
                </button>
              );
            })}
          </div>

          {/* Results Table */}
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-slate-50 text-slate-400 uppercase tracking-wider text-[9px]">
                    <th className="px-3 py-2 text-left font-semibold w-8">#</th>
                    <th className="px-3 py-2 text-left font-semibold">Status</th>
                    <th className="px-3 py-2 text-left font-semibold">Item Code</th>
                    <th className="px-3 py-2 text-left font-semibold">Description</th>
                    <th className="px-3 py-2 text-left font-semibold">Group</th>
                    <th className="px-3 py-2 text-right font-semibold">PT Qty</th>
                    <th className="px-3 py-2 text-right font-semibold">CRR Qty</th>
                    <th className="px-3 py-2 text-right font-semibold">Diff</th>
                    <th className="px-3 py-2 text-left font-semibold">UOM</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((item, i) => {
                    const statusConfig = {
                      match: { bg: '', text: 'text-emerald-600', badge: 'bg-emerald-100 text-emerald-700', label: 'Match' },
                      qty_mismatch: { bg: 'bg-red-50/50', text: 'text-red-600', badge: 'bg-red-100 text-red-700', label: 'Mismatch' },
                      pt_only: { bg: 'bg-amber-50/50', text: 'text-amber-600', badge: 'bg-amber-100 text-amber-700', label: 'PT Only' },
                      crr_only: { bg: 'bg-blue-50/50', text: 'text-blue-600', badge: 'bg-blue-100 text-blue-700', label: 'CRR Only' },
                    };
                    const cfg = statusConfig[item.status] || statusConfig.match;
                    const diff = (item.pt_qty || 0) - (item.crr_qty || 0);
                    return (
                      <tr key={item.code} className={`${cfg.bg} ${i % 2 === 0 ? '' : 'bg-slate-50/30'} hover:bg-blue-50/30 transition-colors`}
                        data-testid={`compare-row-${item.code}`}>
                        <td className="px-3 py-2 text-slate-400">{i + 1}</td>
                        <td className="px-3 py-2">
                          <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${cfg.badge}`}>{cfg.label}</span>
                        </td>
                        <td className="px-3 py-2 font-mono font-semibold text-slate-700">{item.code}</td>
                        <td className="px-3 py-2 text-slate-600 max-w-[250px] truncate" title={item.description}>{item.description}</td>
                        <td className="px-3 py-2 text-slate-500">{item.group || '-'}</td>
                        <td className={`px-3 py-2 text-right font-semibold ${item.pt_qty != null ? 'text-amber-600' : 'text-slate-300'}`}>
                          {item.pt_qty != null ? item.pt_qty : '-'}
                        </td>
                        <td className={`px-3 py-2 text-right font-semibold ${item.crr_qty != null ? 'text-emerald-600' : 'text-slate-300'}`}>
                          {item.crr_qty != null ? item.crr_qty : '-'}
                        </td>
                        <td className={`px-3 py-2 text-right font-bold ${
                          diff === 0 && item.status === 'match' ? 'text-slate-300' :
                          diff > 0 ? 'text-amber-500' : diff < 0 ? 'text-blue-500' : 'text-slate-400'
                        }`}>
                          {item.status === 'pt_only' || item.status === 'crr_only' ? '-' :
                           diff === 0 ? '0' : (diff > 0 ? `+${diff}` : diff)}
                        </td>
                        <td className="px-3 py-2 text-slate-400">{item.uom || '-'}</td>
                      </tr>
                    );
                  })}
                  {filtered.length === 0 && (
                    <tr><td colSpan={9} className="px-3 py-8 text-center text-slate-400">No items in this category</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
